// Notifications dans l'application servies au compte usager : GET /usager/notifications (canaux
// application et portail, toutes collectivités confondues), non lues en évidence, marquage de
// lecture par POST /usager/notifications/:id/lue, actualisation ; les SMS et courriels suivent le
// canal de chaque démarche et le canal préféré du profil.
import { Bandeau, Bouton, EtatVide, Squelette, classes } from '@cc/ui';
import { BellOff, RefreshCw } from 'lucide-react';
import { Link } from 'react-router';
import { requete } from '../../api/client';
import { AffichageErreur } from '../../commun/EtatRequete';
import { dateHeure, pluriel } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { CLES, marquerNotificationLue, useNotificationsUsager } from './api';
import { EnTeteCitoyen, ExigeConnexion, Section, TexteStatut } from './commun';
import { CLE_PROFIL_USAGER, useSessionUsager } from './session/FournisseurSessionUsager';
import type { NotificationUsager, ProfilUsager } from './types';
import commun from './citoyen.module.css';
import styles from './notifications.module.css';

/** Page du portail où mène une notification, selon l'objet qu'elle concerne. */
export function lienObjet(n: Pick<NotificationUsager, 'objet_type' | 'objet_id'>): { href: string; libelle: string } | null {
  if (!n.objet_id) return null;
  switch (n.objet_type) {
    case 'dossier':
      return { href: `/citoyen/mes-dossiers/${n.objet_id}`, libelle: 'Voir la demande' };
    case 'rendez_vous':
      return { href: '/citoyen/rendez-vous', libelle: 'Mes rendez-vous' };
    case 'reclamation':
      return { href: '/citoyen/reclamations', libelle: 'Mes réclamations' };
    case 'signalement':
      return { href: `/citoyen/signalements/${n.objet_id}`, libelle: 'Suivre le signalement' };
    case 'intervention':
      return { href: '/citoyen/signalements', libelle: 'Mes signalements' };
    case 'representation':
      return { href: '/citoyen/compte/representations', libelle: 'Mes représentations' };
    default:
      return null;
  }
}

function Notification({ n, marquer, enCours }: { n: NotificationUsager; marquer: (id: string) => void; enCours: boolean }) {
  const nouvelle = n.lue_le === null;
  const lien = lienObjet(n);
  return (
    <li className={classes(styles.entree, nouvelle && styles.nouvelle)}>
      <div className={styles.enTete}>
        <h3 className={styles.titre}>{n.titre}</h3>
        <TexteStatut libelle={nouvelle ? 'Nouvelle' : 'Lue'} ton={nouvelle ? 'info' : 'neutre'} />
      </div>
      <span className={styles.meta}>
        {n.collectivite.nom} · {dateHeure(n.cree_le)}
      </span>
      {n.contenu ? <p className={styles.contenu}>{n.contenu}</p> : null}
      {nouvelle || lien ? (
        <div className={styles.actions}>
          {nouvelle ? (
            <Bouton variante="secondaire" petit disabled={enCours} onClick={() => marquer(n.id)} aria-label={`Marquer comme lue : ${n.titre}`}>
              Marquer comme lue
            </Bouton>
          ) : null}
          {lien ? <Link to={lien.href}>{lien.libelle}</Link> : null}
        </div>
      ) : null}
    </li>
  );
}

function Canaux({ profil }: { profil: ProfilUsager }) {
  const changer = useAction({
    executer: (canal: 'sms' | 'courriel' | null) => requete<ProfilUsager>('/usager/profil', { methode: 'PUT', corps: { canal_prefere: canal } }),
    succes: 'Canal préféré enregistré.',
    invalider: [[...CLE_PROFIL_USAGER]],
  });
  const courant = profil.canal_prefere;
  return (
    <Section titre="Canal préféré">
      <p className={commun.aide}>
        Chaque démarche prévoit ses canaux (portail, SMS, courriel). Votre canal préféré sert pour les codes et les rappels.
        {!profil.telephone ? ' Aucun téléphone n\'est enregistré : le SMS n\'est pas disponible.' : ''}
        {!profil.courriel ? ' Aucun courriel n\'est enregistré : ajoutez-en un dans votre profil pour le choisir.' : ''}
      </p>
      {changer.messageErreur ? <Bandeau type="erreur">{changer.messageErreur}</Bandeau> : null}
      <div role="group" aria-label="Canal préféré" className={commun.bascule}>
        <button type="button" aria-pressed={courant === 'sms'} disabled={!profil.telephone || changer.enCours} onClick={() => changer.lancer('sms')}>
          SMS
        </button>
        <button type="button" aria-pressed={courant === 'courriel'} disabled={!profil.courriel || changer.enCours} onClick={() => changer.lancer('courriel')}>
          Courriel
        </button>
        <button type="button" aria-pressed={courant === null} disabled={changer.enCours} onClick={() => changer.lancer(null)}>
          Selon la démarche
        </button>
      </div>
    </Section>
  );
}

export function PageNotifications() {
  const { profil } = useSessionUsager();
  const notifications = useNotificationsUsager();
  const lecture = useAction({
    executer: (id: string) => marquerNotificationLue(id),
    succes: 'Notification marquée comme lue.',
    invalider: [[...CLES.notifications]],
  });
  const donnees = notifications.data;
  return (
    <>
      <EnTeteCitoyen titre="Notifications" sousTitre="Ce qui vous a été envoyé dans l'application, par chaque collectivité." />
      <ExigeConnexion>
        <Section titre="Dans l'application">
          {notifications.isPending ? (
            <Squelette lignes={3} />
          ) : notifications.error ? (
            <AffichageErreur erreur={notifications.error} onReessayer={() => void notifications.refetch()} fonction="Les notifications" />
          ) : donnees && donnees.elements.length === 0 ? (
            <EtatVide titre="Aucune notification" icone={BellOff}>
              <p>Vous serez prévenu ici à chaque changement de statut d&apos;une demande, réponse à une réclamation ou rendez-vous confirmé.</p>
            </EtatVide>
          ) : donnees ? (
            <>
              <p className={styles.resume}>{donnees.non_lues > 0 ? pluriel(donnees.non_lues, 'notification non lue', 'notifications non lues') : 'Tout est lu.'}</p>
              {lecture.messageErreur ? <Bandeau type="erreur">{lecture.messageErreur}</Bandeau> : null}
              <ul className={styles.liste} aria-label="Notifications">
                {donnees.elements.map((n) => (
                  <Notification key={n.id} n={n} marquer={lecture.lancer} enCours={lecture.enCours} />
                ))}
              </ul>
            </>
          ) : null}
          <div className={commun.ligneBoutons}>
            <Bouton variante="discret" icone={RefreshCw} onClick={() => void notifications.refetch()} chargement={notifications.isFetching && !notifications.isPending} libelleChargement="Actualisation">
              Actualiser
            </Bouton>
          </div>
        </Section>
        {profil ? <Canaux profil={profil} /> : null}
      </ExigeConnexion>
    </>
  );
}
